import api from "../api";

export function supportsPush() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function mobilePwaNeedsInstallation() {
  const userAgent = navigator.userAgent || "";
  const ipadDesktopMode = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  const mobileTarget = /Android|iPad|iPhone|iPod/i.test(userAgent) || ipadDesktopMode;
  if (!mobileTarget) return false;
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || navigator.standalone === true;
  return !standalone;
}

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map(character => character.charCodeAt(0)));
}

async function activeRegistration() {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return registration;
}

export async function enablePush() {
  if (!supportsPush()) throw new Error("Dieser Browser unterstützt Web Push nicht.");
  if (mobilePwaNeedsInstallation()) throw new Error("Installieren Sie zuerst die App auf Ihrem Home-Bildschirm.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Benachrichtigungen wurden nicht erlaubt.");

  return syncPushSubscription();
}

export async function syncPushSubscription() {
  if (!supportsPush() || Notification.permission !== "granted") return null;

  const keyResponse = await api.getVapidPublicKey();
  if (!keyResponse.body?.enabled || !keyResponse.body?.publicKey) {
    throw new Error("Web Push ist am Server noch nicht konfiguriert.");
  }

  const registration = await activeRegistration();
  if (!registration) throw new Error("Der Service Worker ist noch nicht aktiv. Bitte laden Sie die installierte App neu.");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResponse.body.publicKey),
    });
  }
  await api.subscribePush(subscription.toJSON());
  return subscription;
}

export async function disablePush() {
  if (!supportsPush()) return;
  const registration = await activeRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  let apiError = null;
  try {
    await api.unsubscribePush({ endpoint: subscription.endpoint });
  } catch (error) {
    apiError = error;
  }
  await subscription.unsubscribe();
  if (apiError) throw apiError;
}

export async function pushStatus() {
  if (!supportsPush()) return "unsupported";
  if (mobilePwaNeedsInstallation()) return "requires_install";
  if (Notification.permission === "denied") return "denied";
  const registration = await activeRegistration();
  if (!registration) return "unavailable";
  return (await registration.pushManager.getSubscription()) ? "enabled" : "available";
}
